/**
 * Schnellsuche in der Kopfzeile.
 *
 * Bernd kennt die Plätze beim Namen, nicht über den Weg Kunde → Liste →
 * Platz. Auf dem Handy im Auto ist jeder Zwischenschritt einer zu viel.
 * Deshalb ein Feld, das Kunden und Spielplätze in einer Liste findet.
 *
 * Das Tippen wird gebremst: ohne Verzögerung liefe bei jedem Buchstaben eine
 * Anfrage, und bei schlechter Verbindung überholen sich die Antworten. 250 ms
 * sind lang genug, um Tastendrücke zusammenzufassen, und kurz genug, dass es
 * sich nicht zäh anfühlt.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSuche } from '../api/abfragen'

export function Suche({ beimSpringen }: { beimSpringen?: () => void }) {
  const [eingabe, setzeEingabe] = useState('')
  const [gebremst, setzeGebremst] = useState('')
  const [offen, setzeOffen] = useState(false)
  const [markiert, setzeMarkiert] = useState(0)
  const feld = useRef<HTMLInputElement>(null)
  const huelle = useRef<HTMLDivElement>(null)
  const navigieren = useNavigate()

  useEffect(() => {
    const uhr = window.setTimeout(() => setzeGebremst(eingabe), 250)
    return () => window.clearTimeout(uhr)
  }, [eingabe])

  const treffer = useSuche(gebremst)
  const liste = treffer.data ?? []

  // Klick daneben schließt die Liste. Ohne das bleibt sie stehen, während man
  // schon woanders arbeitet.
  useEffect(() => {
    const daneben = (e: MouseEvent) => {
      if (huelle.current && !huelle.current.contains(e.target as Node)) setzeOffen(false)
    }
    document.addEventListener('mousedown', daneben)
    return () => document.removeEventListener('mousedown', daneben)
  }, [])

  function springen(t: (typeof liste)[number]) {
    setzeOffen(false)
    setzeEingabe('')
    feld.current?.blur()
    beimSpringen?.()
    // Ein Gerät hat keine eigene Seite: der Sprung geht auf seinen Platz.
    navigieren(
      t.art === 'kunde'
        ? `/kunden/${t.id}`
        : t.art === 'geraet'
          ? `/spielplaetze/${t.spielplatz_id}`
          : `/spielplaetze/${t.id}`,
    )
  }

  return (
    <div ref={huelle} className="relative w-full">
      <input
        ref={feld}
        type="search"
        value={eingabe}
        onChange={(e) => {
          setzeEingabe(e.target.value)
          setzeOffen(true)
          setzeMarkiert(0)
        }}
        onFocus={() => setzeOffen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setzeOffen(false)
            feld.current?.blur()
          } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            setzeMarkiert((m) => Math.min(m + 1, liste.length - 1))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setzeMarkiert((m) => Math.max(m - 1, 0))
          } else if (e.key === 'Enter' && liste[markiert]) {
            e.preventDefault()
            springen(liste[markiert])
          }
        }}
        placeholder="Kunde oder Spielplatz suchen"
        aria-label="Kunde oder Spielplatz suchen"
        aria-expanded={offen && liste.length > 0}
        role="combobox"
        aria-controls="suchtreffer"
        autoComplete="off"
        className="!min-h-11 !py-1.5"
      />

      {offen && gebremst.trim().length >= 2 && (
        <div
          id="suchtreffer"
          role="listbox"
          className="absolute inset-x-0 top-full z-40 mt-1 max-h-[60vh] overflow-y-auto rounded-feld border-2 border-slate-300 bg-white shadow-lg"
        >
          {treffer.isLoading ? (
            <p className="p-3 text-slate-600">Sucht …</p>
          ) : liste.length === 0 ? (
            <p className="p-3 text-slate-600">Nichts gefunden für „{gebremst.trim()}"</p>
          ) : (
            <ul>
              {liste.map((t, i) => (
                <li key={`${t.art}-${t.id}`}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={i === markiert}
                    onMouseEnter={() => setzeMarkiert(i)}
                    onClick={() => springen(t)}
                    className={`flex w-full items-center gap-3 border-b border-slate-100 p-3 text-left last:border-b-0 ${
                      i === markiert ? 'bg-marke-50' : ''
                    }`}
                  >
                    <span
                      aria-hidden
                      className="w-6 shrink-0 text-center text-lg text-slate-500"
                      title={
                        t.art === 'kunde'
                          ? 'Kunde'
                          : t.art === 'geraet'
                            ? 'Gerät'
                            : 'Spielplatz'
                      }
                    >
                      {t.art === 'kunde' ? '⌗' : t.art === 'geraet' ? '⚙' : '⌂'}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-semibold">{t.titel}</span>
                      {t.unterzeile && (
                        <span className="block truncate text-base text-slate-600">
                          {t.unterzeile}
                        </span>
                      )}
                    </span>
                    {t.offene_maengel > 0 && (
                      <span className="marke shrink-0 bg-stufe-3-grund text-stufe-3">
                        {t.offene_maengel}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
